"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import {
  DEMO_SHELL_SPECS,
  DemoFrameContext,
  useDemoFrame,
  useLandingFrameMode,
  type DemoViewportMode,
} from "./demo-viewport-context";
import { BRAND_COLORS, DEVICE, hexToRgba } from "@tami/tokens";

interface DeviceFrameProps {
  children: React.ReactNode;
  className?: string;
}

const BEZEL_SHADOW =
  `0 32px 64px ${hexToRgba(BRAND_COLORS.black, 0.7)}, 0 0 0 1px ${hexToRgba(BRAND_COLORS.white, 0.06)}, inset 0 1px 0 ${hexToRgba(BRAND_COLORS.white, 0.1)}`;

export function DeviceFrame({ children, className }: DeviceFrameProps) {
  const mode = useLandingFrameMode();
  const shell = DEMO_SHELL_SPECS[mode];

  return (
    <DemoFrameContext.Provider value={{ mode, shell }}>
      <div
        data-demo-device={mode}
        className={cn("relative mx-auto w-full", className, frameWidthClass(mode))}
      >
        {mode === "mobile" ? (
          <PhoneFrame>{children}</PhoneFrame>
        ) : mode === "tablet" ? (
          <TabletFrame>{children}</TabletFrame>
        ) : (
          <DesktopFrame>{children}</DesktopFrame>
        )}
      </div>
    </DemoFrameContext.Provider>
  );
}

/*
 * Largura do frame na página. O notebook precisa caber inteiro na mesma tela
 * que o seletor de conta e a fileira de abas — cortado, ele esconde justamente
 * o que a demo quer mostrar. Como a tela é 16/9, cada 100px de largura a menos
 * devolve ~56px de altura, e é por aqui que se ajusta o conjunto.
 */
function frameWidthClass(mode: DemoViewportMode): string {
  if (mode === "mobile") return "w-full max-w-[320px]";
  if (mode === "tablet") return "w-full max-w-[640px]";
  return "w-full max-w-[1040px]";
}

/**
 * A tela do device é uma janela, não um viewport: o app replicado é montado na
 * largura para a qual ele foi desenhado (`DEMO_SHELL_SPECS`) e a tela inteira é
 * reduzida por transform até caber no frame.
 *
 * Sem isso o app era comprimido — layout de tela larga espremido nos ~1170px
 * físicos do frame — e a demo passava a impressão de tela apertada, com menos
 * coisa cabendo do que cabe de verdade. A proporção da tela sai do próprio
 * spec, então escalar pela largura acerta a altura junto.
 */
function DemoScreen({
  children,
  roundedClass,
}: {
  children: React.ReactNode;
  roundedClass: string;
}) {
  const { shell } = useDemoFrame();
  const screenRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState<number | null>(null);

  useEffect(() => {
    const el = screenRef.current;
    if (!el) return;
    const compute = () => {
      if (el.clientWidth > 0) setScale(el.clientWidth / shell.width);
    };
    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(el);
    return () => observer.disconnect();
  }, [shell.width]);

  return (
    <div
      ref={screenRef}
      data-demo-screen
      className={cn("relative overflow-hidden bg-background", roundedClass)}
      // A proporção sai do mesmo spec que dimensiona o conteúdo: fixada à mão
      // ela podia divergir, e a tela cortaria ou sobraria na vertical.
      style={{ aspectRatio: `${shell.width} / ${shell.height}` }}
    >
      <div
        // Superfície na resolução lógica: é aqui que modais e sheets devem
        // ser portados, e não na moldura física — fora daqui eles não
        // acompanham a escala e aparecem maiores que a tela atrás deles.
        data-demo-surface
        className="absolute left-0 top-0 origin-top-left"
        style={{
          width: shell.width,
          height: shell.height,
          transform: `scale(${scale ?? 1})`,
          // Antes da primeira medida a escala seria 1 e a tela apareceria
          // gigante por um quadro.
          visibility: scale === null ? "hidden" : undefined,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function PhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="relative rounded-[44px] bg-device-bezel p-2"
      style={{ boxShadow: BEZEL_SHADOW }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-4 z-10 h-[26px] w-[96px] -translate-x-1/2 rounded-full bg-black"
      />
      <DemoScreen roundedClass="rounded-[36px]">
        {children}
      </DemoScreen>
    </div>
  );
}

function TabletFrame({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="relative rounded-[28px] bg-device-bezel p-3"
      style={{ boxShadow: BEZEL_SHADOW }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-2.5 z-10 size-2 -translate-x-1/2 rounded-full bg-device-camera"
      />
      <DemoScreen roundedClass="rounded-[20px]">
        {children}
      </DemoScreen>
    </div>
  );
}

const LAPTOP_ALUMINUM =
  `linear-gradient(180deg, ${DEVICE.lid[0]} 0%, ${DEVICE.lid[1]} 52%, ${DEVICE.lid[2]} 100%)`;
const LAPTOP_BASE_ALUMINUM =
  `linear-gradient(180deg, ${DEVICE.base[0]} 0%, ${DEVICE.base[1]} 60%, ${DEVICE.base[2]} 100%)`;
const LAPTOP_SHADOW =
  `0 44px 80px -24px ${hexToRgba(BRAND_COLORS.black, 0.85)}, 0 12px 28px -12px ${hexToRgba(BRAND_COLORS.black, 0.6)}, inset 0 1px 0 ${hexToRgba(BRAND_COLORS.white, 0.12)}`;

function DesktopFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative">
      {/* Tampa (lid) de alumínio com bezel preto e notch */}
      <div
        className="relative rounded-[20px] border border-white/[0.08] p-[3px]"
        style={{ background: LAPTOP_ALUMINUM, boxShadow: LAPTOP_SHADOW }}
      >
        <div className="relative rounded-[17px] bg-device-screen-border px-[10px] pb-[10px] pt-[18px]">
          {/* Notch (câmera) centralizado no bezel superior */}
          <div
            aria-hidden
            className="pointer-events-none absolute left-1/2 top-0 z-10 flex h-[13px] w-[122px] -translate-x-1/2 items-center justify-center rounded-b-[8px] bg-black"
          >
            <span className="size-[5px] rounded-full bg-device-webcam ring-1 ring-white/5" />
          </div>

          {/* Janela do navegador (conteúdo da tela) */}
          <div className="overflow-hidden rounded-[8px]">
            <div className="flex items-center gap-0 border-b border-white/5 bg-device-toolbar px-4 py-3">
              <div className="mr-4 flex items-center gap-1.5">
                <span className="flex size-3 rounded-full bg-device-traffic-close" />
                <span className="flex size-3 rounded-full bg-device-traffic-minimize" />
                <span className="flex size-3 rounded-full bg-device-traffic-zoom" />
              </div>
              <div className="flex flex-1 items-center justify-center">
                <div className="flex w-[40%] items-center gap-2 rounded-md bg-device-bezel px-3 py-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-device-traffic-zoom opacity-80" />
                  <span className="font-mono text-[11px] text-white/30">tamiapp.com</span>
                </div>
              </div>
              <div className="w-[68px]" />
            </div>

            <DemoScreen roundedClass="rounded-none">
              {children}
            </DemoScreen>
          </div>
        </div>
      </div>

      {/* Lábio inferior da carcaça: dá o "pezinho" de laptop fechado */}
      <div
        aria-hidden
        className="relative left-1/2 h-[11px] w-[101.5%] -translate-x-1/2 rounded-b-[12px] border-x border-b border-white/[0.06]"
        style={{ background: LAPTOP_BASE_ALUMINUM }}
      >
        {/* Recorte central onde se levanta a tampa */}
        <div className="absolute left-1/2 top-0 h-[7px] w-[88px] -translate-x-1/2 rounded-b-[7px] bg-black/45" />
      </div>
    </div>
  );
}
